import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Onboarding step enumeration
export type OnboardingStep = 
  | 'welcome'
  | 'permissions' 
  | 'profile'
  | 'tutorial'
  | 'completion';

// Permission status type
export type PermissionStatus = 'pending' | 'granted' | 'denied';

// Permission types
export interface OnboardingPermissions {
  location: PermissionStatus;
  camera: PermissionStatus;
  notifications: PermissionStatus;
}

// Profile data interface
export interface OnboardingProfileData {
  department: string;
  dailyTarget: number;
  workingHours: string;
  emergencyContact: string;
  preferences: {
    notifications: boolean;
    hapticFeedback: boolean;
    autoSync: boolean;
  };
}

// Tutorial progress tracking
export interface TutorialProgress {
  visitCreation: boolean;
  evidenceCapture: boolean;
  reportGeneration: boolean;
  navigation: boolean;
}

// Local onboarding state interface (more detailed than types.ts version)
interface OnboardingState {
  currentStep: OnboardingStep;
  completedSteps: OnboardingStep[];
  isCompleted: boolean;
  isLoading: boolean;
  permissions: OnboardingPermissions;
  profileData: OnboardingProfileData;
  tutorialProgress: TutorialProgress;
  errors: Record<string, string>;
  startTime: number | null;
  completionTime: number | null;
}

// Initial state - ONBOARDING DISABLED: Marked as completed by default to streamline user experience
// To re-enable onboarding: change isCompleted to false, completedSteps to [], completionTime to null
const initialState: OnboardingState = {
  currentStep: 'completion',
  completedSteps: ['welcome', 'permissions', 'profile', 'tutorial', 'completion'],
  isCompleted: true, // DISABLED: Onboarding marked as completed by default
  isLoading: false,
  permissions: {
    location: 'granted', // Default to granted since onboarding is disabled
    camera: 'granted',
    notifications: 'granted',
  },
  profileData: {
    department: '',
    dailyTarget: 8,
    workingHours: '09:00-17:00',
    emergencyContact: '',
    preferences: {
      notifications: true,
      hapticFeedback: true,
      autoSync: true,
    },
  },
  tutorialProgress: {
    visitCreation: true, // Mark all tutorial steps as completed
    evidenceCapture: true,
    reportGeneration: true,
    navigation: true,
  },
  errors: {},
  startTime: null,
  completionTime: Date.now(), // Mark as completed at initialization
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Start onboarding process
    startOnboarding: (state) => {
      state.startTime = Date.now();
      state.currentStep = 'welcome';
      state.completedSteps = [];
      state.isCompleted = false;
      state.errors = {};
    },

    // Navigate to specific step
    navigateToStep: (state, action: PayloadAction<OnboardingStep>) => {
      state.currentStep = action.payload;
      state.errors = {};
    },

    // Mark current step as completed and move to next
    completeStep: (state, action: PayloadAction<OnboardingStep>) => {
      const step = action.payload;
      
      // Add to completed steps if not already there
      if (!state.completedSteps.includes(step)) {
        state.completedSteps.push(step);
      }

      // Navigate to next step
      const stepOrder: OnboardingStep[] = ['welcome', 'permissions', 'profile', 'tutorial', 'completion'];
      const currentIndex = stepOrder.indexOf(step);
      
      if (currentIndex < stepOrder.length - 1) {
        state.currentStep = stepOrder[currentIndex + 1];
      } else {
        // All steps completed
        state.isCompleted = true;
        state.completionTime = Date.now();
      }
    },

    // Go back to previous step
    goBackStep: (state) => {
      const stepOrder: OnboardingStep[] = ['welcome', 'permissions', 'profile', 'tutorial', 'completion'];
      const currentIndex = stepOrder.indexOf(state.currentStep);
      
      if (currentIndex > 0) {
        state.currentStep = stepOrder[currentIndex - 1];
      }
      state.errors = {};
    },

    // Update permission status
    updatePermission: (state, action: PayloadAction<{ 
      permission: keyof OnboardingPermissions; 
      status: PermissionStatus 
    }>) => {
      const { permission, status } = action.payload;
      state.permissions[permission] = status;
    },

    // Update multiple permissions at once
    updatePermissions: (state, action: PayloadAction<Partial<OnboardingPermissions>>) => {
      state.permissions = { ...state.permissions, ...action.payload };
    },

    // Update profile data
    updateProfileData: (state, action: PayloadAction<Partial<OnboardingProfileData>>) => {
      state.profileData = { 
        ...state.profileData, 
        ...action.payload,
        preferences: {
          ...state.profileData.preferences,
          ...action.payload.preferences,
        },
      };
    },

    // Update tutorial progress
    updateTutorialProgress: (state, action: PayloadAction<Partial<TutorialProgress>>) => {
      state.tutorialProgress = { ...state.tutorialProgress, ...action.payload };
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error for specific field/step
    setError: (state, action: PayloadAction<{ field: string; message: string }>) => {
      const { field, message } = action.payload;
      state.errors[field] = message;
    },

    // Clear specific error
    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },

    // Clear all errors
    clearAllErrors: (state) => {
      state.errors = {};
    },

    // Reset onboarding to initial state
    resetOnboarding: () => {
      return { ...initialState };
    },

    // Skip onboarding (for returning users or testing)
    skipOnboarding: (state) => {
      state.isCompleted = true;
      state.completionTime = Date.now();
      state.completedSteps = ['welcome', 'permissions', 'profile', 'tutorial', 'completion'];
    },

    // Mark onboarding as completed
    completeOnboarding: (state) => {
      state.isCompleted = true;
      state.completionTime = Date.now();
      state.currentStep = 'completion';
      
      // Ensure all steps are marked as completed
      const allSteps: OnboardingStep[] = ['welcome', 'permissions', 'profile', 'tutorial', 'completion'];
      state.completedSteps = allSteps;
    },
  },
});

// Export actions
export const {
  startOnboarding,
  navigateToStep,
  completeStep,
  goBackStep,
  updatePermission,
  updatePermissions,
  updateProfileData,
  updateTutorialProgress,
  setLoading,
  setError,
  clearError,
  clearAllErrors,
  resetOnboarding,
  skipOnboarding,
  completeOnboarding,
} = onboardingSlice.actions;

// Selectors
export const selectOnboardingState = (state: { onboarding: OnboardingState }) => state.onboarding;
export const selectCurrentStep = (state: { onboarding: OnboardingState }) => state.onboarding.currentStep;
export const selectIsOnboardingCompleted = (state: { onboarding: OnboardingState }) => state.onboarding.isCompleted;
export const selectOnboardingProgress = (state: { onboarding: OnboardingState }) => {
  const totalSteps = 5;
  const completedCount = state.onboarding.completedSteps.length;
  return Math.round((completedCount / totalSteps) * 100);
};
export const selectPermissions = (state: { onboarding: OnboardingState }) => state.onboarding.permissions;
export const selectProfileData = (state: { onboarding: OnboardingState }) => state.onboarding.profileData;
export const selectOnboardingErrors = (state: { onboarding: OnboardingState }) => state.onboarding.errors;

export default onboardingSlice.reducer;